import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext';
import { getLicense, PLAN_LABEL } from '../config/licenseKeys';
import api from '../api/client';
import './Onboarding.css';

const BIZ_TYPES = [
  'Agency', 'IT / Tech', 'Retail', 'Coaching', 'Healthcare',
  'Restaurant', 'Construction', 'Freelancer', 'Other',
];

const TOTAL_STEPS = 5; // 0=biz 1=company 2=bank 3=team 4=launch

export default function Onboarding() {
  const { theme } = useTheme();
  const { update: updateCompany } = useCompany();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const logoRef = useRef();

  const license = getLicense();
  const planSeats = license ? license.seats : null; // null = unlimited / no license

  const [step, setStep]       = useState(0);
  const [bizType, setBizType] = useState('Agency');

  const [co, setCo] = useState(() => {
    let seed = {};
    try { seed = JSON.parse(localStorage.getItem('ryzenos_company') || '{}') || {}; } catch {}
    return {
      contactName: seed.contactName || '',
      name: seed.name || '',
      users: planSeats != null ? String(planSeats) : (seed.users || ''),
      gstin: seed.gstin || '',
      email: seed.email || '',
      phone: seed.phone || '',
      website: seed.website || '',
      address: seed.address || '',
      city: seed.city || '',
      state: seed.state || '',
      pincode: seed.pincode || '',
      country: 'India',
      logo: seed.logo || '',
      services: Array.isArray(seed.services) ? seed.services : [],
      bank: seed.bank || { accName: '', accNumber: '', bank: '', branch: '', ifsc: '' },
      username: seed.username || '',
      password: '',
    };
  });
  const setCof  = (k, v) => setCo(p => ({ ...p, [k]: v }));
  const setBank = (k, v) => setCo(p => ({ ...p, bank: { ...p.bank, [k]: v } }));

  const [svcInput, setSvcInput] = useState('');
  const addService = () => {
    const v = svcInput.trim();
    if (!v || co.services.includes(v)) { setSvcInput(''); return; }
    setCo(p => ({ ...p, services: [...p.services, v] }));
    setSvcInput('');
  };
  const removeService = (s) => setCo(p => ({ ...p, services: p.services.filter(x => x !== s) }));

  const onLogo = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCof('logo', reader.result);
    reader.readAsDataURL(f);
  };

  // Team members -- capped to the plan's seat limit
  const [email, setEmail] = useState('');
  const [members, setMembers] = useState([{ email: co.email || 'you@company.in', role: 'Super Admin' }]);
  const [teamMsg, setTeamMsg] = useState('');
  const seatLimit = planSeats; // null = unlimited
  const addMember = () => {
    if (!email.trim()) return;
    if (seatLimit != null && members.length >= seatLimit) {
      setTeamMsg(`Your ${license ? PLAN_LABEL[license.plan] : ''} plan includes ${seatLimit} member${seatLimit>1?'s':''}. Upgrade to add more.`);
      return;
    }
    setMembers(p => [...p, { email: email.trim(), role: 'User' }]);
    setEmail('');
    setTeamMsg('');
  };
  const removeMember = (i) => setMembers(p => p.filter((_, idx) => idx !== i));

  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activated, setActivated] = useState(false);

  const persistLocal = (profile) => {
    updateCompany(profile);
    localStorage.setItem('ryzenos_onboarded', 'true');
    localStorage.setItem('ryzenos_company', JSON.stringify(profile));
    localStorage.setItem('ryzenos_company_profile', JSON.stringify(profile));
    if (Array.isArray(co.services) && co.services.length) {
      localStorage.setItem('ryzenos_services', JSON.stringify(co.services));
    }
    localStorage.removeItem('ryzenos_activating');
  };

  const handleLaunch = async () => {
    // Everyone added here (beyond the owner) joins with access role 'User'.
    const employees = members.slice(1).map(m => ({ ...m, role: 'User' }));
    const profile = { ...co, bizType, country: 'India', members: employees };

    if (isAuthenticated) {
      persistLocal(profile);
      navigate('/dashboard');
      return;
    }

    // Activation flow: the license key creates the company + admin account.
    if (!co.username.trim() || !co.password.trim()) { setErr('Choose a username and password for your login.'); return; }
    if (co.password.length < 6) { setErr('Password must be at least 6 characters.'); return; }
    const lic = getLicense();
    setSubmitting(true); setErr('');
    try {
      const res = await api.auth.activate({
        key: lic && lic.key,
        company: {
          name: co.name, contactName: co.contactName, email: co.email, phone: co.phone,
          gstin: co.gstin, businessType: bizType, address: co.address, city: co.city,
          state: co.state, pincode: co.pincode, logo: co.logo, bank: co.bank, services: co.services,
        },
        admin: { name: co.contactName || co.name || co.username, username: co.username.trim(), password: co.password },
      });
      api.setToken(res.token);
      const initials = (res.user.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
      sessionStorage.setItem('ryzenos_session', JSON.stringify({ ...res.user, initials }));
      persistLocal(profile);
      localStorage.removeItem('ryzenos_license');
      setSubmitting(false);
      setActivated(true); // show the success popup, then enter the workspace
      setTimeout(() => window.location.assign('/dashboard'), 2600);
    } catch (e) {
      setSubmitting(false);
      setErr(e.message || 'Activation failed. Please try again.');
    }
  };

  const seatHint = seatLimit == null ? 'Unlimited members' : `${members.length} of ${seatLimit} members`;

  if (activated) {
    return (
      <div className="ob-wrap" data-theme={theme}>
        <div className="ob-glow" />
        <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div style={{textAlign:'center',maxWidth:420,animation:'obPop .4s ease'}}>
            <div style={{width:72,height:72,borderRadius:'50%',background:'rgba(34,197,94,.15)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px'}}>
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <div style={{fontSize:24,fontWeight:800,marginBottom:8}}>Workspace activated!</div>
            <div style={{opacity:.7,fontSize:14,lineHeight:1.6,marginBottom:22}}>Your key is now linked to <b>{co.name || 'your company'}</b>. Taking you into your workspace…</div>
            <button className="ob-next" onClick={()=>window.location.assign('/dashboard')}>Enter workspace</button>
          </div>
        </div>
        <style>{`@keyframes obPop{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:scale(1)}}`}</style>
      </div>
    );
  }

  return (
    <div className="ob-wrap" data-theme={theme}>
      <div className="ob-glow" />
      <div className="ob-inner">
        <div className="ob-prog">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <React.Fragment key={i}>
              <div className={`ob-dot${step===i?' active':step>i?' done':''}`} />
              {i < TOTAL_STEPS - 1 && <div className={`ob-gap${step>i?' done':''}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 0: Business type */}
        {step === 0 && (
          <>
            <div className="ob-tag">Getting started{license ? ` · ${PLAN_LABEL[license.plan]} plan` : ''}</div>
            <div className="ob-title">What kind of business<br />are you running?</div>
            <div className="ob-biz-grid">
              {BIZ_TYPES.map(b => (
                <div key={b} className={`ob-biz-card${bizType===b?' selected':''}`} onClick={() => setBizType(b)}>
                  <div className="ob-biz-tick">✓</div>
                  <div className="ob-biz-name">{b}</div>
                </div>
              ))}
            </div>
            <div className="ob-footer">
              <span className="ob-hint">Tap to select</span>
              <button className="ob-next" onClick={() => setStep(1)}>Continue</button>
            </div>
          </>
        )}

        {/* Step 1: Company details */}
        {step === 1 && (
          <>
            <div className="ob-tag">Company profile</div>
            <div className="ob-title">Tell us about<br />your company</div>
            <div className="ob-co-form">
              <div className="ob-field-row">
                <div className="ob-field-grp">
                  <label className="ob-field-lbl">Your name *</label>
                  <input className="ob-field-inp" placeholder="Contact person" value={co.contactName} onChange={e=>setCof('contactName',e.target.value)} />
                </div>
                <div className="ob-field-grp">
                  <label className="ob-field-lbl">Company name *</label>
                  <input className="ob-field-inp" placeholder="Your company" value={co.name} onChange={e=>setCof('name',e.target.value)} />
                </div>
              </div>
              <div className="ob-field-row ob-field-row--3">
                <div className="ob-field-grp">
                  <label className="ob-field-lbl">Number of users</label>
                  <input className="ob-field-inp" value={co.users} onChange={e=>setCof('users',e.target.value)} disabled={planSeats != null} title={planSeats!=null?'Set by your plan':''} />
                </div>
                <div className="ob-field-grp">
                  <label className="ob-field-lbl">GSTIN (optional)</label>
                  <input className="ob-field-inp" placeholder="22AAAAA0000A1Z5" value={co.gstin} onChange={e=>setCof('gstin',e.target.value)} />
                </div>
                <div className="ob-field-grp">
                  <label className="ob-field-lbl">Phone</label>
                  <input className="ob-field-inp" placeholder="+91 XXXXX XXXXX" value={co.phone} onChange={e=>setCof('phone',e.target.value)} />
                </div>
              </div>
              <div className="ob-field-row">
                <div className="ob-field-grp">
                  <label className="ob-field-lbl">Business email</label>
                  <input className="ob-field-inp" type="email" placeholder="hello@company.in" value={co.email} onChange={e=>setCof('email',e.target.value)} />
                </div>
                <div className="ob-field-grp">
                  <label className="ob-field-lbl">Website (optional)</label>
                  <input className="ob-field-inp" placeholder="www.company.in" value={co.website} onChange={e=>setCof('website',e.target.value)} />
                </div>
              </div>
              <div className="ob-field-grp ob-field-grp--full">
                <label className="ob-field-lbl">Address</label>
                <input className="ob-field-inp" placeholder="Street address, building" value={co.address} onChange={e=>setCof('address',e.target.value)} />
              </div>
              <div className="ob-field-row ob-field-row--3">
                <div className="ob-field-grp"><label className="ob-field-lbl">City</label><input className="ob-field-inp" value={co.city} onChange={e=>setCof('city',e.target.value)} /></div>
                <div className="ob-field-grp"><label className="ob-field-lbl">State</label><input className="ob-field-inp" value={co.state} onChange={e=>setCof('state',e.target.value)} /></div>
                <div className="ob-field-grp"><label className="ob-field-lbl">Pincode</label><input className="ob-field-inp" value={co.pincode} onChange={e=>setCof('pincode',e.target.value)} /></div>
              </div>

              <div className="ob-field-grp ob-field-grp--full">
                <label className="ob-field-lbl">Services (optional)</label>
                <div className="ob-svc-row">
                  <input className="ob-field-inp" placeholder="e.g. Web Development" value={svcInput}
                    onChange={e=>setSvcInput(e.target.value)} onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); addService(); } }} />
                  <button type="button" className="ob-svc-add" onClick={addService}>Add</button>
                </div>
                {co.services.length>0 && (
                  <div className="ob-svc-tags">
                    {co.services.map(s => (
                      <span key={s} className="ob-svc-tag">{s}<i onClick={()=>removeService(s)}>×</i></span>
                    ))}
                  </div>
                )}
              </div>

              <div className="ob-field-grp ob-field-grp--full">
                <label className="ob-field-lbl">Company logo (optional)</label>
                <div className="ob-logo-row">
                  {co.logo
                    ? <img src={co.logo} alt="logo" className="ob-logo-prev" />
                    : <div className="ob-logo-ph">No logo</div>}
                  <input ref={logoRef} type="file" accept="image/*" style={{display:'none'}} onChange={onLogo} />
                  <button type="button" className="ob-svc-add" onClick={()=>logoRef.current.click()}>{co.logo?'Change':'Upload'}</button>
                  {co.logo && <button type="button" className="ob-skip-link" onClick={()=>setCof('logo','')}>Remove</button>}
                  <span className="ob-skip-note">You can add or change this later in Settings.</span>
                </div>
              </div>
            </div>
            <div className="ob-footer">
              <button className="ob-back" onClick={() => setStep(0)}>Back</button>
              <button className="ob-next" onClick={() => setStep(2)}>Continue</button>
            </div>
          </>
        )}

        {/* Step 2: Bank details (optional) */}
        {step === 2 && (
          <>
            <div className="ob-tag">Payment details</div>
            <div className="ob-title">Where should clients<br />pay you?</div>
            <div className="ob-sub-note">These appear on the invoices you send. You can skip this and add it later from Settings.</div>
            <div className="ob-co-form">
              <div className="ob-field-row">
                <div className="ob-field-grp"><label className="ob-field-lbl">Account holder name</label><input className="ob-field-inp" placeholder="As per bank records" value={co.bank.accName} onChange={e=>setBank('accName',e.target.value)} /></div>
                <div className="ob-field-grp"><label className="ob-field-lbl">Account number</label><input className="ob-field-inp" value={co.bank.accNumber} onChange={e=>setBank('accNumber',e.target.value)} /></div>
              </div>
              <div className="ob-field-row ob-field-row--3">
                <div className="ob-field-grp"><label className="ob-field-lbl">Bank</label><input className="ob-field-inp" placeholder="Federal Bank" value={co.bank.bank} onChange={e=>setBank('bank',e.target.value)} /></div>
                <div className="ob-field-grp"><label className="ob-field-lbl">Branch</label><input className="ob-field-inp" value={co.bank.branch} onChange={e=>setBank('branch',e.target.value)} /></div>
                <div className="ob-field-grp"><label className="ob-field-lbl">IFSC code</label><input className="ob-field-inp" placeholder="FDRL0001316" value={co.bank.ifsc} onChange={e=>setBank('ifsc',e.target.value.toUpperCase())} /></div>
              </div>
            </div>
            <div className="ob-footer">
              <button className="ob-back" onClick={() => setStep(1)}>Back</button>
              <div style={{display:'flex',gap:10,alignItems:'center'}}>
                <button className="ob-skip-link" onClick={() => setStep(3)}>Skip for now</button>
                <button className="ob-next" onClick={() => setStep(3)}>Continue</button>
              </div>
            </div>
          </>
        )}

        {/* Step 3: Team */}
        {step === 3 && (
          <>
            <div className="ob-tag">Your team · {seatHint}</div>
            <div className="ob-title">Who's joining you?</div>
            <div className="ob-sub-note" style={{marginTop:-16,marginBottom:18}}>You're the account owner. Add your teammates by email — they'll join as employees.</div>
            <input className="ob-inp" placeholder="Email address" value={email}
              onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addMember()} />
            <button className="ob-add-btn" onClick={addMember}>
              {seatLimit != null && members.length >= seatLimit ? `Plan limit reached (${seatLimit})` : 'Add member'}
            </button>
            {teamMsg && <div className="ob-team-msg">{teamMsg}</div>}
            <div className="ob-members">
              {members.map((m, i) => {
                const isOwner = i === 0;
                const primary = isOwner ? (co.contactName || co.email || 'You') : m.email;
                const secondary = isOwner && co.contactName && co.email ? co.email : '';
                return (
                  <div key={i} className="ob-member">
                    <div className="ob-member-info">
                      <div className="ob-av">{(primary[0]||'?').toUpperCase()}</div>
                      <span className="ob-member-name">{primary}{secondary && <span style={{opacity:.5,fontWeight:400}}> · {secondary}</span>}</span>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span className="ob-role-tag">{isOwner ? 'Owner (you)' : 'Employee'}</span>
                      {i>0 && <span className="ob-skip-link" onClick={()=>removeMember(i)}>Remove</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="ob-footer">
              <button className="ob-back" onClick={() => setStep(2)}>Back</button>
              <button className="ob-next" onClick={() => setStep(4)}>Continue</button>
            </div>
          </>
        )}

        {/* Step 4: Launch */}
        {step === 4 && (
          <>
            <div className="ob-launch">
              <div className="ob-launch-title">You're all set{co.name ? `, ${co.name}` : ''}</div>
              <div className="ob-launch-sub">Your workspace is ready to go</div>
            </div>
            <div className="ob-review">
              {[
                { label: 'Contact',       val: co.contactName || '—' },
                { label: 'Company',       val: co.name || '—' },
                { label: 'Business type', val: bizType },
                { label: 'Plan',          val: license ? PLAN_LABEL[license.plan] : 'Trial' },
                { label: 'Users',         val: seatLimit == null ? 'Unlimited' : `${members.length} of ${seatLimit}` },
                { label: 'Bank details',  val: co.bank.accNumber ? 'Added' : 'Skipped' },
              ].map(r => (
                <div key={r.label} className="ob-review-item">
                  <span className="ob-review-label">{r.label}</span>
                  <span className="ob-review-val">{r.val}</span>
                </div>
              ))}
            </div>

            {!isAuthenticated && (
              <div className="ob-co-form" style={{ marginTop: 18 }}>
                <div className="ob-tag" style={{ margin: 0 }}>Create your login</div>
                <div className="ob-field-row">
                  <div className="ob-field-grp">
                    <label className="ob-field-lbl">Username *</label>
                    <input className="ob-field-inp" placeholder="e.g. yourname" value={co.username} onChange={e=>setCof('username', e.target.value.replace(/\s/g,''))} autoComplete="username" />
                  </div>
                  <div className="ob-field-grp">
                    <label className="ob-field-lbl">Password *</label>
                    <input className="ob-field-inp" type="password" placeholder="At least 6 characters" value={co.password} onChange={e=>setCof('password', e.target.value)} autoComplete="new-password" />
                  </div>
                </div>
                <div className="ob-skip-note">You'll use this to sign in to your workspace.</div>
                {err && <div className="ob-team-msg" style={{ color: '#ff6b6b', borderColor: 'rgba(255,107,107,.3)', background: 'rgba(255,107,107,.08)' }}>{err}</div>}
              </div>
            )}

            <div className="ob-footer">
              <button className="ob-back" onClick={() => setStep(3)} disabled={submitting}>Back</button>
              <button className="ob-next" onClick={handleLaunch} disabled={submitting}>{submitting ? 'Setting up…' : (isAuthenticated ? 'Launch workspace' : 'Finish setup')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
